import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { friendsChanged } from "@/lib/friends";

const POLL_MS = 15000;
const SEEN_KEY = "jangle.dm.toasted";

// Pops up "New message from …" when a friend messages you while Jangle is
// open, on every page including games. Phones get a push notification only
// when the app is in the background, and website players get no push at all.
export default function FriendMessageToasts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pathRef = useRef(pathname);
  pathRef.current = pathname;
  const userId = user?.id;

  const check = useCallback(async () => {
    if (document.visibilityState === "hidden") return;
    let latest;
    try {
      ({ data: { latest } } = await api.get("/friends/badge"));
    } catch {
      return;
    }
    if (!latest?.id) return;
    let seen = null;
    try { seen = sessionStorage.getItem(SEEN_KEY); } catch { /* ignore */ }
    if (seen === latest.id) return;
    try { sessionStorage.setItem(SEEN_KEY, latest.id); } catch { /* ignore */ }
    friendsChanged();
    const chat = `/friends/${latest.from}`;
    // Already reading it, or looking at the Messages list.
    if (pathRef.current === chat || pathRef.current === "/messages") return;
    toast(`New message from ${latest.username}`, {
      id: `dm-${latest.from}`,
      description: latest.text.length > 90 ? `${latest.text.slice(0, 87)}…` : latest.text,
      duration: 8000,
      action: { label: "Open", onClick: () => navigate(chat) },
    });
  }, [navigate]);

  useEffect(() => {
    if (!userId) return undefined;
    check();
    const t = setInterval(check, POLL_MS);
    document.addEventListener("visibilitychange", check);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", check);
    };
  }, [userId, check]);

  return null;
}
